import React, { useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { History, Plus, Trash2, PenLine, AlertTriangle, Lock } from 'lucide-react';
import { AuditEntry, FeatureFlagEntry } from '@/client/types/feature-flags';
import { useFeatureFlagAuditQuery } from '@/client/api/feature-flags/feature-flags.query';
import { relativeTimeFromSeconds, absoluteTimeFromSeconds } from './utils';

interface FeatureFlagAuditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, only entries for this flag name are shown. */
  filterName?: string | null;
  /** Optional uid → display-name resolver. */
  resolveActor?: (uid: number) => string | undefined;
}

// ─── before → after diff ───────────────────────────────────────────────────────

const fmt = (v: unknown): string => {
  if (v == null) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const DiffRow: React.FC<{ label: string; before: unknown; after: unknown }> = ({
  label,
  before,
  after,
}) => {
  const changed = JSON.stringify(before) !== JSON.stringify(after);
  if (!changed) return null;
  return (
    <div className="grid grid-cols-[64px_1fr] gap-2 text-xs">
      <span className="font-medium text-gray-500">{label}</span>
      <span className="flex flex-wrap items-center gap-1.5">
        <code className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">
          {fmt(before)}
        </code>
        <span className="text-gray-400">→</span>
        <code className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">{fmt(after)}</code>
      </span>
    </div>
  );
};

const EntryDiff: React.FC<{ before: FeatureFlagEntry | null; after: FeatureFlagEntry | null }> = ({
  before,
  after,
}) => (
  <div className="mt-2 space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/70 p-2.5">
    <DiffRow label="enabled" before={before?.enabled} after={after?.enabled} />
    <DiffRow label="desc" before={before?.description} after={after?.description} />
    <DiffRow label="conditions" before={before?.conditions ?? null} after={after?.conditions ?? null} />
    {/* When one side is entirely absent (create/delete), fall back to a summary. */}
    {(!before || !after) && (
      <p className="text-[11px] text-gray-400">
        {!before ? 'Override created' : 'Override removed (reverted to default)'}
      </p>
    )}
  </div>
);

const ACTION_META: Record<string, { icon: React.ReactNode; badge: string; label: string }> = {
  upsert: {
    icon: <PenLine className="h-3.5 w-3.5" />,
    badge: 'bg-blue-100 text-blue-700',
    label: 'Updated',
  },
  delete: {
    icon: <Trash2 className="h-3.5 w-3.5" />,
    badge: 'bg-orange-100 text-orange-700',
    label: 'Reverted',
  },
};

const AuditRow: React.FC<{ entry: AuditEntry; resolveActor?: (uid: number) => string | undefined }> = ({
  entry,
  resolveActor,
}) => {
  const isCreate = entry.action === 'upsert' && !entry.before;
  const meta = isCreate
    ? { icon: <Plus className="h-3.5 w-3.5" />, badge: 'bg-green-100 text-green-700', label: 'Created' }
    : ACTION_META[entry.action] ?? ACTION_META.upsert;

  const actorLabel =
    entry.actor == null
      ? 'system'
      : resolveActor?.(entry.actor) ?? `uid ${entry.actor}`;

  return (
    <li className="relative pl-6">
      <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-gray-300 ring-1 ring-gray-200" />
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}
        >
          {meta.icon}
          {meta.label}
        </span>
        <span className="font-mono text-xs text-gray-700">{entry.name}</span>
        <span
          className="ml-auto text-xs text-gray-400"
          title={absoluteTimeFromSeconds(entry.ts)}
        >
          {relativeTimeFromSeconds(entry.ts)}
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-gray-400">by {actorLabel}</p>
      <EntryDiff before={entry.before} after={entry.after} />
    </li>
  );
};

const FeatureFlagAuditDrawer: React.FC<FeatureFlagAuditDrawerProps> = ({
  isOpen,
  onClose,
  filterName,
  resolveActor,
}) => {
  const { data, isLoading, isError, error } = useFeatureFlagAuditQuery(200, isOpen);

  const entries = useMemo(() => {
    const all = data?.entries ?? [];
    return filterName ? all.filter((e) => e.name === filterName) : all;
  }, [data, filterName]);

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-gray-100 px-6 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-lg font-semibold">
            <History className="h-5 w-5 text-gray-500" />
            {filterName ? 'Flag history' : 'Change log'}
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500">
            {filterName ? (
              <span className="font-mono text-xs">{filterName}</span>
            ) : (
              'Every override create, update and revert, newest first.'
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : isError && (error as any)?.status === 403 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-gray-500">
              <Lock className="h-6 w-6 text-gray-400" />
              <p className="font-medium text-gray-700">Change log unavailable</p>
              <p className="max-w-xs text-xs text-gray-500">
                Viewing the audit log requires the{' '}
                <code className="rounded bg-gray-100 px-1 py-0.5">system.feature_flags.manage</code>{' '}
                permission.
              </p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-red-600">
              <AlertTriangle className="h-6 w-6" />
              {(error as any)?.message ?? 'Failed to load the audit log.'}
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              No changes recorded{filterName ? ' for this flag' : ''} yet.
            </div>
          ) : (
            <ul className="space-y-5 border-l border-gray-200">
              {entries.map((entry, i) => (
                <AuditRow key={`${entry.name}-${entry.ts}-${i}`} entry={entry} resolveActor={resolveActor} />
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FeatureFlagAuditDrawer;
